import { fetchScreenshotDownloadUrl } from './api';

// Use local agent URL for screenshots in development, production URL in production
const BASE_URL = process.env.REACT_APP_WS_URL
    ? `${process.env.REACT_APP_WS_URL}/test-automation/`
    : "https://ap-agent.qa-path.com/test-automation/";

const screenshotUrlCache = new Map();
const screenshotRetryCount = new Map();
const MAX_SCREENSHOT_RETRIES = 3;

/**
 * API payloads sometimes return a string key, or `{ url }` / `{ path }`.
 * Prefer stable S3 key over presigned url when both exist.
 */
export function normalizeScreenshotInput(raw) {
    if (raw == null) return null;
    if (typeof raw === 'string') return raw.trim() || null;
    if (typeof raw === 'object') {
        return raw.path || raw.key || raw.s3Key || raw.url || null;
    }
    return null;
}

/**
 * Prefer replay stepDetails, then merged step — matches RunningExecutionView / ExecutionView.
 */
export function extractAutomationScreenshotPath(replayDetail, displayStep = {}) {
    const candidates = [
        replayDetail?.screenshot,
        replayDetail?.result?.screenshot,
        replayDetail?.result?.details?.screenshot,
        replayDetail?.result?.result?.screenshot,
        replayDetail?.result?.result?.screenshotPath,
        replayDetail?.result?.details?.screenshotPath,
        replayDetail?.screenshotPath,
        replayDetail?.result?.screenshotPath,
        displayStep?.screenshot,
        displayStep?.screenshotPath,
        displayStep?.details?.screenshotPath,
        displayStep?.result?.result?.screenshot,
        displayStep?.result?.result?.screenshotPath,
        displayStep?.executionGroup?.mainAction?.details?.screenshotPath,
    ];
    for (const c of candidates) {
        const n = normalizeScreenshotInput(c);
        if (n) return n;
    }
    return null;
}

const isS3FileKey = (value) =>
    typeof value === 'string' &&
    value.includes('/') &&
    !value.startsWith('/') &&
    !/^[A-Za-z]:[\\/]/.test(value) &&
    !value.startsWith('http://') &&
    !value.startsWith('https://') &&
    !value.startsWith('data:image/');

/**
 * Stable S3 object key from a key string or presigned / API URL.
 */
export function toS3FileKey(value) {
    const normalized = normalizeScreenshotInput(value);
    if (!normalized) return null;
    if (isS3FileKey(normalized)) return normalized;
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
        return extractS3FileKeyFromUrl(normalized);
    }
    return null;
}

function extractS3FileKeyFromUrl(urlString) {
    try {
        const url = new URL(urlString);
        const apiMatch = url.pathname.match(/\/s3\/screenshot\/([^/]+)\/download-url$/i);
        if (apiMatch) {
            const key = decodeURIComponent(apiMatch[1]);
            return isS3FileKey(key) ? key : null;
        }
        const pathKey = decodeURIComponent(url.pathname.replace(/^\//, ''));
        if (isS3FileKey(pathKey)) return pathKey;
        const qaIdx = pathKey.indexOf('qa-path/');
        if (qaIdx >= 0) {
            const key = pathKey.slice(qaIdx);
            return isS3FileKey(key) ? key : null;
        }
    } catch {
        // not a valid URL
    }
    return null;
}

const getCachedUrl = (fileKey) => {
    const entry = screenshotUrlCache.get(fileKey);
    if (!entry) return null;
    return typeof entry === 'string' ? entry : entry.url;
};

export const invalidateScreenshotCache = (fileKey) => {
    if (fileKey) screenshotUrlCache.delete(fileKey);
};

const fetchPresignedUrl = async (fileKey) => {
    const urlData = await fetchScreenshotDownloadUrl(fileKey);
    if (urlData?.downloadUrl) {
        screenshotUrlCache.set(fileKey, { url: urlData.downloadUrl });
        screenshotRetryCount.delete(fileKey);
        return urlData.downloadUrl;
    }
    return null;
};

export const resolveScreenshotUrl = async (screenshotValue, baseUrl = BASE_URL, options = {}) => {
    const bypassCache = options.bypassCache === true;
    const normalized = normalizeScreenshotInput(screenshotValue);
    if (!normalized) {
        return { url: null, loading: false };
    }
    screenshotValue = normalized;

    if (screenshotValue.startsWith('data:image/')) {
        return { url: screenshotValue, loading: false };
    }

    if (
        screenshotValue.length > 500 &&
        !screenshotValue.includes('/') &&
        !screenshotValue.includes('\\') &&
        /^[A-Za-z0-9+/=\s]+$/.test(screenshotValue)
    ) {
        return { url: `data:image/jpeg;base64,${screenshotValue}`, loading: false };
    }

    // Valid presigned URL: use as-is on resolve (no presign API). Refetch only via refreshScreenshotUrl on img error.
    if (
        !bypassCache &&
        (screenshotValue.startsWith('http://') || screenshotValue.startsWith('https://'))
    ) {
        return { url: screenshotValue, loading: false };
    }

    const fileKeyFromInput = toS3FileKey(screenshotValue);
    if (fileKeyFromInput) {
        const fileKey = fileKeyFromInput;
        if (!bypassCache) {
            const cachedUrl = getCachedUrl(fileKey);
            if (cachedUrl) {
                return { url: cachedUrl, loading: false };
            }
        }

        try {
            const url = await fetchPresignedUrl(fileKey);
            if (url) {
                return { url, loading: false };
            }
        } catch (error) {
            console.warn('S3 fetch failed, using fallback:', error.message);
        }
    }

    if (screenshotValue.startsWith('http://') || screenshotValue.startsWith('https://')) {
        return { url: screenshotValue, loading: false };
    }

    if (!bypassCache && screenshotUrlCache.has(screenshotValue)) {
        const cachedUrl = getCachedUrl(screenshotValue);
        if (cachedUrl) {
            return { url: cachedUrl, loading: false };
        }
    }

    const fallbackUrl = `${baseUrl}${screenshotValue}`;
    screenshotUrlCache.set(screenshotValue, { url: fallbackUrl });
    return { url: fallbackUrl, loading: false };
};

/**
 * On-demand refetch when <img onError> (e.g. expired presigned URL).
 */
export const refreshScreenshotUrl = async (screenshotValue, baseUrl = BASE_URL) => {
    const fileKey = toS3FileKey(screenshotValue);
    if (!fileKey) {
        return { url: null, loading: false };
    }

    const retries = screenshotRetryCount.get(fileKey) || 0;
    if (retries >= MAX_SCREENSHOT_RETRIES) {
        return { url: null, loading: false };
    }

    invalidateScreenshotCache(fileKey);
    const result = await resolveScreenshotUrl(fileKey, baseUrl, { bypassCache: true });
    if (result.url) {
        screenshotRetryCount.delete(fileKey);
        return result;
    }
    screenshotRetryCount.set(fileKey, retries + 1);
    return { url: null, loading: false };
};

export const clearScreenshotCache = () => {
    screenshotUrlCache.clear();
    screenshotRetryCount.clear();
};
